import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { 
  Play, Square, Clock, Plus, Trash2, DollarSign, 
  Timer, Loader2, ChevronDown, ChevronUp
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from './ui/dialog';

const TicketTimeTracker = ({ ticketId, clientId }) => {
  const [activeTimer, setActiveTimer] = useState(null);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [timeEntries, setTimeEntries] = useState([]);
  const [totalSeconds, setTotalSeconds] = useState(0);
  const [totalBillable, setTotalBillable] = useState(0);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [showEntries, setShowEntries] = useState(false);
  const [stopDialogOpen, setStopDialogOpen] = useState(false);
  const [manualDialogOpen, setManualDialogOpen] = useState(false);
  const [description, setDescription] = useState('');
  const [isBillable, setIsBillable] = useState(true);
  const [manualMinutes, setManualMinutes] = useState(15);
  const [clientBillingInfo, setClientBillingInfo] = useState(null);
  
  const timerIntervalRef = useRef(null);

  // Load initial data
  useEffect(() => {
    loadData();
    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [ticketId]);

  // Timer tick effect
  useEffect(() => {
    if (activeTimer) {
      timerIntervalRef.current = setInterval(() => {
        const startTime = new Date(activeTimer.start_time);
        const now = new Date();
        const elapsed = Math.floor((now - startTime) / 1000);
        setElapsedSeconds(elapsed);
      }, 1000);
    } else {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
      setElapsedSeconds(0);
    }

    return () => {
      if (timerIntervalRef.current) {
        clearInterval(timerIntervalRef.current);
      }
    };
  }, [activeTimer]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load time entries
      const entriesRes = await api.get(`/tickets/${ticketId}/time-entries`);
      setTimeEntries(entriesRes.data.entries || []);
      setTotalSeconds(entriesRes.data.total_seconds || 0);
      setTotalBillable(entriesRes.data.total_billable || 0);

      // Load active timer
      const timerRes = await api.get(`/tickets/${ticketId}/active-timer`);
      setActiveTimer(timerRes.data.active_timer);

      // Load client billing info if we have a client
      if (clientId) {
        try {
          const clientRes = await api.get(`/clients/${clientId}`);
          setClientBillingInfo({
            hourly_rate: clientRes.data.hourly_rate || 0,
            billing_increment: clientRes.data.billing_increment || 15,
            currency: clientRes.data.currency || 'GBP'
          });
        } catch (e) {
          // Client might not exist or no billing settings
        }
      }
    } catch (error) {
      console.error('Failed to load time tracking data:', error);
    } finally {
      setLoading(false);
    }
  };

  const startTimer = async () => {
    setActionLoading(true);
    try {
      const res = await api.post(`/tickets/${ticketId}/timer/start`);
      setActiveTimer(res.data.timer);
      toast.success('Timer started');
    } catch (error) {
      toast.error('Failed to start timer');
    } finally {
      setActionLoading(false);
    }
  };

  const stopTimer = async () => {
    setActionLoading(true);
    try {
      const res = await api.post(`/tickets/${ticketId}/timer/stop`, {
        description,
        is_billable: isBillable
      });
      setActiveTimer(null);
      setDescription('');
      setStopDialogOpen(false);
      toast.success('Timer stopped and time logged');
      loadData(); // Reload to get updated entries
    } catch (error) {
      toast.error('Failed to stop timer');
    } finally {
      setActionLoading(false);
    }
  };

  const addManualEntry = async () => {
    if (!manualMinutes || manualMinutes <= 0) {
      toast.error('Please enter valid minutes');
      return;
    }

    setActionLoading(true);
    try {
      await api.post(`/tickets/${ticketId}/time-entries/manual`, {
        manual_minutes: parseInt(manualMinutes),
        description,
        is_billable: isBillable
      });
      setManualDialogOpen(false);
      setDescription('');
      setManualMinutes(15);
      toast.success('Time entry added');
      loadData();
    } catch (error) {
      toast.error('Failed to add time entry');
    } finally {
      setActionLoading(false);
    }
  };

  const deleteEntry = async (entryId) => {
    if (!window.confirm('Are you sure you want to delete this time entry?')) return;

    try {
      await api.delete(`/tickets/${ticketId}/time-entries/${entryId}`);
      toast.success('Time entry deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete time entry');
    }
  };

  // Format seconds to HH:MM:SS
  const formatTime = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;
    return `${hrs.toString().padStart(2, '0')}:${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Format seconds to readable duration
  const formatDuration = (seconds) => {
    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    
    if (hrs > 0 && mins > 0) return `${hrs}h ${mins}m`;
    if (hrs > 0) return `${hrs}h`;
    if (mins > 0) return `${mins}m`;
    return '< 1m';
  };

  // Format currency
  const formatCurrency = (amount, currency = 'GBP') => {
    const symbols = { GBP: '£', USD: '$', EUR: '€' };
    const symbol = symbols[currency] || currency + ' ';
    return `${symbol}${amount.toFixed(2)}`;
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="py-4">
          <div className="flex items-center justify-center">
            <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center justify-between text-sm">
          <span className="flex items-center gap-2">
            <Timer className="w-4 h-4" />
            Time Tracking
          </span>
          {clientBillingInfo && clientBillingInfo.hourly_rate > 0 && (
            <Badge variant="outline" className="text-xs font-normal">
              {formatCurrency(clientBillingInfo.hourly_rate, clientBillingInfo.currency)}/hr
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        {/* Timer Display */}
        <div className="flex items-center justify-between bg-gray-50 rounded-lg p-3">
          <div className="flex items-center gap-3">
            {activeTimer ? (
              <>
                <div className="relative">
                  <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse"></div>
                </div>
                <div>
                  <div className="text-2xl font-mono font-bold text-gray-900">
                    {formatTime(elapsedSeconds)}
                  </div>
                  <div className="text-xs text-gray-500">Recording...</div>
                </div>
              </>
            ) : (
              <div>
                <div className="text-2xl font-mono font-bold text-gray-400">
                  00:00:00
                </div>
                <div className="text-xs text-gray-400">Timer stopped</div>
              </div>
            )}
          </div>

          <div className="flex items-center gap-2">
            {activeTimer ? (
              <Button
                onClick={() => setStopDialogOpen(true)}
                variant="destructive"
                size="sm"
                className="gap-1"
                disabled={actionLoading}
              >
                <Square className="w-3 h-3" />
                Stop
              </Button>
            ) : (
              <Button
                onClick={startTimer}
                size="sm"
                className="gap-1 bg-green-600 hover:bg-green-700"
                disabled={actionLoading}
              >
                {actionLoading ? (
                  <Loader2 className="w-3 h-3 animate-spin" />
                ) : (
                  <Play className="w-3 h-3" />
                )}
                Start
              </Button>
            )}
            <Button
              onClick={() => setManualDialogOpen(true)}
              variant="outline"
              size="sm"
              className="gap-1"
              disabled={activeTimer || actionLoading}
              title="Add manual time entry"
            >
              <Plus className="w-3 h-3" />
            </Button>
          </div>
        </div>

        {/* Total Time Summary */}
        <div className="flex items-center justify-between text-sm">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-1">
              <Clock className="w-4 h-4 text-gray-400" />
              <span className="font-medium">{formatDuration(totalSeconds + elapsedSeconds)}</span>
              <span className="text-gray-500">total</span>
            </div>
            {totalBillable > 0 && (
              <div className="flex items-center gap-1 text-green-600">
                <DollarSign className="w-4 h-4" />
                <span className="font-medium">
                  {formatCurrency(totalBillable, clientBillingInfo?.currency || 'GBP')}
                </span>
              </div>
            )}
          </div>
          
          {timeEntries.length > 0 && (
            <button
              onClick={() => setShowEntries(!showEntries)}
              className="text-blue-600 hover:text-blue-800 text-xs flex items-center gap-1"
            >
              {timeEntries.length} entries
              {showEntries ? <ChevronUp className="w-3 h-3" /> : <ChevronDown className="w-3 h-3" />}
            </button>
          )}
        </div>

        {/* Time Entries List */}
        {showEntries && timeEntries.length > 0 && (
          <div className="border rounded-lg divide-y max-h-48 overflow-auto">
            {timeEntries.map((entry) => (
              <div key={entry.id} className="p-2 hover:bg-gray-50 text-xs">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{formatDuration(entry.duration_seconds)}</span>
                    {entry.is_billable && entry.billable_amount > 0 && (
                      <Badge variant="outline" className="text-xs text-green-600 border-green-200">
                        {formatCurrency(entry.billable_amount, clientBillingInfo?.currency || 'GBP')}
                      </Badge>
                    )}
                    {!entry.is_billable && (
                      <Badge variant="outline" className="text-xs text-gray-400">
                        Non-billable
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-gray-400">
                      {new Date(entry.start_time).toLocaleDateString()}
                    </span>
                    <button
                      onClick={() => deleteEntry(entry.id)}
                      className="text-red-500 hover:text-red-700"
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                </div>
                {entry.description && (
                  <div className="text-gray-500 mt-1 truncate">{entry.description}</div>
                )}
                <div className="text-gray-400 text-xs mt-1">by {entry.user_name}</div>
              </div>
            ))}
          </div>
        )}

        {/* No billing rate warning */}
        {clientId && (!clientBillingInfo || clientBillingInfo.hourly_rate === 0) && (
          <div className="text-xs text-amber-600 bg-amber-50 rounded p-2">
            ⚠️ No hourly rate set for this client. Time will be tracked but not billed.
          </div>
        )}
      </CardContent>

      {/* Stop Timer Dialog */}
      <Dialog open={stopDialogOpen} onOpenChange={setStopDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Square className="w-5 h-5 text-red-500" />
              Stop Timer
            </DialogTitle>
            <DialogDescription>
              You've been working for <strong>{formatTime(elapsedSeconds)}</strong>. 
              Add a description for this time entry.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Description (optional)</label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What did you work on?"
                rows={3}
              />
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isBillable}
                onChange={(e) => setIsBillable(e.target.checked)}
                className="rounded border-gray-300"
              />
              <span className="text-sm">Billable time</span>
              {isBillable && clientBillingInfo && clientBillingInfo.hourly_rate > 0 && (
                <span className="text-xs text-gray-500">
                  (≈ {formatCurrency(
                    (Math.ceil(elapsedSeconds / (clientBillingInfo.billing_increment * 60)) * 
                     clientBillingInfo.billing_increment / 60) * clientBillingInfo.hourly_rate,
                    clientBillingInfo.currency
                  )})
                </span>
              )}
            </label>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setStopDialogOpen(false)}>
              Continue Working
            </Button>
            <Button onClick={stopTimer} disabled={actionLoading} variant="destructive">
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Stop & Log Time
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Entry Dialog */}
      <Dialog open={manualDialogOpen} onOpenChange={setManualDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Add Manual Time Entry
            </DialogTitle>
            <DialogDescription>
              Manually log time spent on this ticket.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1">Time (minutes)</label>
              <Input
                type="number"
                value={manualMinutes}
                onChange={(e) => setManualMinutes(e.target.value)}
                min={1}
                step={clientBillingInfo?.billing_increment || 15}
                placeholder="15"
              />
              <div className="flex gap-2 mt-2">
                {[15, 30, 45, 60, 90, 120].map((mins) => (
                  <button
                    key={mins}
                    onClick={() => setManualMinutes(mins)}
                    className={`px-2 py-1 text-xs rounded border ${
                      parseInt(manualMinutes) === mins 
                        ? 'bg-blue-100 border-blue-300 text-blue-700' 
                        : 'bg-gray-50 border-gray-200 hover:bg-gray-100'
                    }`}
                  >
                    {mins >= 60 ? `${mins / 60}h` : `${mins}m`}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-1">Description (optional)</label>
              <Textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="What did you work on?"
                rows={3}
              />
            </div>

            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="checkbox"
                checked={isBillable}
                onChange={(e) => setIsBillable(e.target.checked)}
                className="rounded border-gray-300"
              />
              <span className="text-sm">Billable time</span>
            </label>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setManualDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={addManualEntry} disabled={actionLoading}>
              {actionLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
              Add Entry
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default TicketTimeTracker;
